import { Button } from '@/components/ui/shadcn/button'
import { trpc } from '@/config/trpc'
import env from '@/config/env'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/shadcn/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/shadcn/dialog'
import { ExternalLink, Trash, TriangleAlert } from 'lucide-react'
import { toast } from 'sonner'
import { useState } from 'react'
import { Input } from '@/components/ui/shadcn/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/shadcn/select'
import { CopyBlock, CopyField } from '@/components/developer/copy-field'

const DOCS_URL = `${env.VITE_API_URL}/docs`

const formatDate = (value: string | Date | null) => {
  if (!value) return 'never'
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function AccountApiPage() {
  const utils = trpc.useUtils()
  const { data: apiKeys, isLoading } = trpc.apiKey.list.useQuery()
  const { mutateAsync: revokeApiKey } = trpc.apiKey.revoke.useMutation()

  const handleRevokeApiKey = async (id: number, name: string) => {
    if (!confirm(`Revoke "${name}"? Anything using this key stops working immediately.`)) return
    try {
      await revokeApiKey({ id })
      await utils.apiKey.list.invalidate()
      toast.success('API key revoked')
    } catch (e) {
      toast.error('Failed to revoke key', {
        description: e instanceof Error ? e.message : 'Unknown error',
      })
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-xl sm:text-2xl">API Keys</CardTitle>
          <CardDescription>
            The CASSI REST API lets your own code do what the app does: read the brand brief,
            generate posts and images, schedule and publish, and read how previous posts performed.
            Each key is scoped to a single brand.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button variant="outline" asChild>
            <a href={DOCS_URL} target="_blank" rel="noreferrer">
              API reference
              <ExternalLink className="h-4 w-4" />
            </a>
          </Button>

          {isLoading && <p className="text-muted-foreground text-sm">Loading…</p>}

          {apiKeys && apiKeys.length === 0 && (
            <p className="text-muted-foreground text-sm">
              No API keys yet. Create one to start calling the API.
            </p>
          )}

          {apiKeys && apiKeys.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground">
                  <th className="pb-2 text-left font-normal">Name</th>
                  <th className="pb-2 text-left font-normal">Brand</th>
                  <th className="pb-2 text-left font-normal">Key</th>
                  <th className="pb-2 text-left font-normal">Last used</th>
                  <th className="pb-2 text-right font-normal">Actions</th>
                </tr>
              </thead>
              <tbody>
                {apiKeys.map(({ id, name, keyPrefix, brandName, lastUsedAt }) => (
                  <tr key={id} className="border-border/60 border-t first:border-t-0">
                    <td className="py-2 text-left">{name}</td>
                    <td className="text-muted-foreground py-2 text-left">{brandName ?? '—'}</td>
                    <td className="py-2 text-left font-mono text-xs">{keyPrefix}…</td>
                    <td className="text-muted-foreground py-2 text-left">
                      {formatDate(lastUsedAt)}
                    </td>
                    <td className="py-2 text-right">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Revoke ${name}`}
                        onClick={() => handleRevokeApiKey(id, name)}>
                        <Trash className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <CreateApiKey />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Quick start</CardTitle>
          <CardDescription>Send the key as a bearer token. Every response is JSON.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <CopyBlock
            label="example request"
            value={`curl ${env.VITE_API_URL}/v1/me \\\n  -H "Authorization: Bearer cassi_api_YOUR_KEY"`}
          />
          <p className="text-muted-foreground text-sm">
            Base URL <span className="text-foreground font-mono">{env.VITE_API_URL}</span>. List
            endpoints are paginated (<span className="font-mono">limit</span>,{' '}
            <span className="font-mono">offset</span>). Rate limits are 120 requests per minute, and
            20 per minute for generation endpoints.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}

const CreateApiKey = () => {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [pickedBrandId, setPickedBrandId] = useState<number | null>(null)
  const [createdKey, setCreatedKey] = useState<string | null>(null)

  const utils = trpc.useUtils()
  const { data: brands } = trpc.brand.list.useQuery()
  const { data: currentBrand } = trpc.brand.getCurrent.useQuery()
  const { mutateAsync: createApiKey, isPending } = trpc.apiKey.create.useMutation()

  // Derived rather than synced into state via an effect: until the user picks
  // a brand explicitly, the active one is the default.
  const brandId = pickedBrandId ?? currentBrand?.id ?? null

  const handleCreateApiKey = async () => {
    if (brandId === null) return
    try {
      const { key } = await createApiKey({ name, brandId })
      await utils.apiKey.list.invalidate()
      setName('')
      // Swap the form for the reveal. The key is not stored in plaintext, so
      // this dialog is the only place it will ever be shown.
      setCreatedKey(key)
    } catch (error) {
      toast.error('Failed to create API key', {
        description: error instanceof Error ? error.message : 'Unknown error occurred',
      })
      setOpen(false)
    }
  }

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) setCreatedKey(null)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button>Create API Key</Button>
      </DialogTrigger>
      <DialogContent>
        {createdKey ? (
          <>
            <DialogHeader>
              <DialogTitle>Copy your key now</DialogTitle>
              <DialogDescription>
                This is the only time it will be shown. CASSI stores a hash, not the key itself, so
                it cannot be recovered later. If you lose it, revoke this key and create another.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <CopyField value={createdKey} label="API key" />
              <p className="text-muted-foreground flex items-start gap-2 text-sm">
                <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  Treat it like a password. Anyone holding it can post on behalf of this brand.
                </span>
              </p>
            </div>
            <DialogFooter>
              <Button onClick={() => handleOpenChange(false)}>Done</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Create API Key</DialogTitle>
              <DialogDescription>
                The key is scoped to a single brand. Calls made with it can only read and modify
                that brand's data.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="API Key name"
              />
              <Select
                value={brandId !== null ? String(brandId) : ''}
                onValueChange={(v) => setPickedBrandId(Number(v))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select brand" />
                </SelectTrigger>
                <SelectContent>
                  {brands?.map((brand) => (
                    <SelectItem key={brand.id} value={String(brand.id)}>
                      {brand.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateApiKey}
                disabled={!name.trim() || brandId === null || isPending}>
                Create
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
